import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
import { isoDateSchema } from './subscription.js';
import { TRIAL_DAYS_MAX, TRIAL_DAYS_MIN } from './trial-days.js';
// ---------------------------------------------------------------------------
// Sign-up with admin approval (registration-approval arch §2.1, §3, §4, §5.3).
//
// A sign-up records a request in `internal.signup_requests`; nothing plays
// until an admin approves it with a tariff and a number of trial days. Every
// date on the wire is a `YYYY-MM-DD` calendar date in UTC — see
// `utils/calendar-date.ts`.
// ---------------------------------------------------------------------------
/** Error codes of the request endpoints, for the admin's error mapping. */
export const SIGNUP_REQUEST_ERROR_CODES = {
    /**
     * 409 — another decision came first (a second admin, or a second click): an
     * approval of an approved request, or a rejection of one no longer waiting.
     */
    SIGNUP_REQUEST_ALREADY_HANDLED: 'SIGNUP_REQUEST_ALREADY_HANDLED',
    /** 404 — no request with that id. */
    SIGNUP_REQUEST_NOT_FOUND: 'SIGNUP_REQUEST_NOT_FOUND',
};
/**
 * Sign-in refusals that concern sign-up (arch §3.1, §3.4). `POST
 * /player/auth/login` answers `403 ACCESS_NOT_OPEN` after the right password
 * when the user has no client membership and a waiting or rejected request —
 * the player shows the waiting screen. No membership and no request stays
 * `403 NO_CLIENT_MEMBERSHIP`, and a wrong password stays `401
 * INVALID_CREDENTIALS`, so nothing reveals a request without the password. A
 * refusal, not a new login `stage`: ADR-042 freezes `playerLoginOutcomeSchema`.
 */
export const PLAYER_LOGIN_ERROR_CODES = {
    ACCESS_NOT_OPEN: 'ACCESS_NOT_OPEN',
};
/** Mirrors the `signup_requests_status_check` CHECK (migration 0127). */
export const signupRequestStatusSchema = z.enum(['waiting', 'approved', 'rejected']);
/**
 * `POST /player/auth/register` → `201`. The request is recorded; no session is
 * issued. `signup_token` is a 30-minute token (audience `player-signup`) that
 * only the details step accepts (below); every other player guard refuses it.
 * `409 EMAIL_IN_USE` for an email already taken in any letter case.
 */
export const playerSignupReceiptSchema = z
    .object({
    signup_token: z.string().min(1),
    expires_at: isoDateTimeSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// The optional details step, «Расскажите о заведении» (arch §3.1, §3.2, §10;
// STORY-008). Both routes take the receipt's `signup_token` as
// `Authorization: Bearer <signup_token>` — never in a query string. The token
// names the request; nothing else on the wire does.
// ---------------------------------------------------------------------------
/** The input caps of «Название заведения» and «Телефон или Telegram»; the table CHECKs enforce the same. */
export const SIGNUP_VENUE_NAME_MAX_LENGTH = 120;
export const SIGNUP_CONTACT_MAX_LENGTH = 200;
/**
 * Refusals of the details step. The player answers `401` and `409` alike with
 * the waiting screen: the request already stands (D-15).
 *
 *   * `401 SIGNUP_TOKEN_INVALID` — no token, an expired one, one that is not a
 *     sign-up token, or one whose request no longer exists.
 *   * `409 SIGNUP_REQUEST_CLOSED` — an admin already approved or rejected the
 *     request; its details can no longer change.
 *   * `422 VENUE_CATEGORY_UNKNOWN` — `format_id` is not an active category (the
 *     code «Кабинет» answers with); `details: [{ field: 'format_id', value }]`.
 */
export const PLAYER_SIGNUP_ERROR_CODES = {
    SIGNUP_TOKEN_INVALID: 'SIGNUP_TOKEN_INVALID',
    SIGNUP_REQUEST_CLOSED: 'SIGNUP_REQUEST_CLOSED',
    VENUE_CATEGORY_UNKNOWN: 'VENUE_CATEGORY_UNKNOWN',
};
/** One venue category the details step offers (D-11). */
export const playerSignupCategorySchema = z
    .object({
    id: uuidSchema,
    /** Stable key; `'other'` is «Прочее». */
    code: z.string(),
    name: z.string(),
})
    .strict();
/**
 * `GET /player/auth/signup/categories` (sign-up token) → `200`. The active
 * venue categories in the order «Кабинет» shows them: by name, «Прочее» last.
 */
export const playerSignupCategoryListSchema = z
    .object({
    items: z.array(playerSignupCategorySchema),
})
    .strict();
/**
 * `PUT /player/auth/signup/details` (sign-up token) → `204`, no body. Replaces
 * the request's three details as a whole while it waits: a field left out is
 * cleared, so send every filled field each time and leave empty ones out (not
 * `null`). Values are trimmed; an empty or blank one is a `400
 * VALIDATION_ERROR`. «Пропустить» sends nothing. On approval the details become
 * the account's company name and contact phone and the venue's name and
 * category.
 */
export const playerSignupDetailsRequestSchema = z
    .object({
    venue_name: z.string().trim().min(1).max(SIGNUP_VENUE_NAME_MAX_LENGTH).optional(),
    /** An `id` from `GET /player/auth/signup/categories`. */
    format_id: uuidSchema.optional(),
    /** Phone or Telegram, free text. */
    contact: z.string().trim().min(1).max(SIGNUP_CONTACT_MAX_LENGTH).optional(),
})
    .strict();
/**
 * `GET /admin/signup-requests` query. Approved requests are accounts and are
 * listed with the accounts, so only `waiting` and `rejected` are listable.
 * Keyset pagination on `(created_at DESC, id DESC)`: pass `next_cursor` back as
 * `cursor`. `limit=1` is enough for the tab counter — `total` does not depend on
 * the page.
 */
export const signupRequestListStatusSchema = signupRequestStatusSchema.exclude(['approved']);
export const signupRequestListQuerySchema = z.object({
    status: signupRequestListStatusSchema.default('waiting'),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
});
/** The venue category a user picked on the details step (STORY-008). */
export const signupRequestCategorySchema = z.object({
    id: uuidSchema,
    name: z.string(),
});
/**
 * One request row. `venue_name`, `category` and `contact` are what the user
 * entered on the details step (STORY-008) — each `null` when left empty or
 * skipped («—»); `decided_at` and `decided_by_name` are set on a rejected
 * request (STORY-007) — `decided_by_name` is `null` when the admin's user row is
 * gone.
 */
export const signupRequestListItemSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    venue_name: z.string().nullable(),
    category: signupRequestCategorySchema.nullable(),
    contact: z.string().nullable(),
    created_at: isoDateTimeSchema,
    decided_at: isoDateTimeSchema.nullable(),
    decided_by_name: z.string().nullable(),
});
/** `total` is the number of requests with the queried status, whatever the page. */
export const signupRequestListResponseSchema = z.object({
    items: z.array(signupRequestListItemSchema),
    next_cursor: z.string().nullable(),
    total: z.number().int().nonnegative(),
});
/**
 * `POST /admin/signup-requests/:id/approve` body. The tariff must exist and be
 * active (`400 TARIFF_NOT_FOUND` / `TARIFF_INACTIVE`).
 */
export const signupRequestApproveRequestSchema = z
    .object({
    tariff_id: uuidSchema,
    trial_days: z.number().int().min(TRIAL_DAYS_MIN).max(TRIAL_DAYS_MAX),
})
    .strict();
/**
 * `200` of the approval. The trial runs from the server's today:
 * `trial_end_date` = today + `trial_days` (the subscription's `period_end`,
 * exclusive), `trial_last_day` = the day before it — «Последний день триала».
 */
export const signupRequestApproveResponseSchema = z.object({
    request_id: uuidSchema,
    account_id: uuidSchema,
    trial_end_date: isoDateSchema,
    trial_last_day: isoDateSchema,
});
/**
 * `POST /admin/signup-requests/:id/reject` body — empty; strict, so a stray key
 * is a `400 VALIDATION_ERROR`.
 */
export const signupRequestRejectRequestSchema = z.object({}).strict();
/**
 * `200` of the rejection (arch §4.2). Only a waiting request is rejected: an
 * approved or already rejected one → `409 SIGNUP_REQUEST_ALREADY_HANDLED`, an
 * unknown id → `404 SIGNUP_REQUEST_NOT_FOUND`. The request moves to
 * «Отклонённые» (`status=rejected`, with `decided_at` and `decided_by_name`),
 * from where it can still be approved (PRD D-10). Nothing else changes: the user
 * keeps the waiting screen, the email stays taken and nobody is emailed.
 */
export const signupRequestRejectResponseSchema = z.object({
    request_id: uuidSchema,
    status: z.literal('rejected'),
});
/**
 * `GET /admin/server-date` — the server's UTC calendar date. The admin never
 * computes today itself; it refetches this when a dialog with dates opens.
 */
export const serverDateResponseSchema = z.object({
    today: isoDateSchema,
});
//# sourceMappingURL=signup.js.map